import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { MultiSelectDropdown } from "../../../components/dropdown/MultiSelectDropdown";
import {
  createVisitor,
  updateVisitor,
  getVisitorById,
} from "../../../service/securityService";
import { Visitor, VisitorFormData, VisitorType } from "../../../types/security_types";
import axiosInstance from "../../../common/axiosInstance";
import { useDepartmentStore, usePremisesStore } from "../../../store/store";

interface VisitorFormProps {
  visitor?: Visitor;
  onSuccess: () => void;
  onCancel: () => void;
}

const VisitorForm: React.FC<VisitorFormProps> = ({
  visitor,
  onSuccess,
  onCancel,
}) => {
  const isEdit = !!visitor;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { departments, fetchAllDepartments } = useDepartmentStore();
  const [zones, setZones] = useState<Array<{ value: string; label: string }>>([]);

  const [formData, setFormData] = useState<VisitorFormData>({
    fullName: "",
    visitorType: "Patient Attendant",
    idType: "Aadhaar",
    idNumber: "",
    mobileNumber: "",
    hostDepartment: "",
    hostDepartmentId: "",
    hostName: "",
    purposeOfVisit: "",
    accessZones: [],
    visitStart: "",
    visitEnd: "",
    idVerified: false,
  });

  useEffect(() => {
    fetchAllDepartments();
    fetchZones();
    if (isEdit && visitor) {
      loadVisitorData();
    }
  }, [visitor, fetchAllDepartments]);

  const fetchZones = async () => {
    try {
      const response = await axiosInstance.get("/api/security/access-zones");
      if (response.data.success) {
        const zonesData = response.data.data || response.data.message || [];
        setZones(
          zonesData.map((zone: any) => ({
            value: zone._id || zone.id,
            label: zone.zoneName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const loadVisitorData = async () => {
    if (!visitor) return;
    try {
      setLoading(true);
      const id = visitor._id || visitor.id;
      if (!id) return;
      const visitorData = await getVisitorById(id);
      setFormData({
        fullName: visitorData.fullName || "",
        visitorType: visitorData.visitorType || "Patient Attendant",
        idType: visitorData.idType || "Aadhaar",
        idNumber: visitorData.idNumber || "",
        mobileNumber: visitorData.mobileNumber || "",
        hostDepartment: visitorData.hostDepartment || "",
        hostDepartmentId: visitorData.hostDepartmentId || "",
        hostName: visitorData.hostName || "",
        purposeOfVisit: visitorData.purposeOfVisit || "",
        accessZones: visitorData.accessZones || [],
        visitStart: visitorData.visitStart
          ? new Date(visitorData.visitStart).toISOString().slice(0, 16)
          : "",
        visitEnd: visitorData.visitEnd
          ? new Date(visitorData.visitEnd).toISOString().slice(0, 16)
          : "",
        idVerified: !!visitorData.idVerified,
      });
    } catch (error: any) {
      console.error("Error loading visitor data:", error);
      toast.error("Failed to load visitor data");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.idType.trim()) {
      newErrors.idType = "ID type is required";
    }

    if (!formData.idNumber.trim()) {
      newErrors.idNumber = "ID number is required";
    }

    const mobDigits = formData.mobileNumber.replace(/\D/g, "");
    if (!mobDigits) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (mobDigits.length !== 10) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }

    if (!formData.hostDepartmentId) {
      newErrors.hostDepartment = "Host department is required";
    }

    if (!formData.accessZones || formData.accessZones.length === 0) {
      newErrors.accessZones = "At least one access zone is required";
    }

    if (!formData.visitStart) {
      newErrors.visitStart = "Visit start date is required";
    }

    if (!formData.visitEnd) {
      newErrors.visitEnd = "Visit end date is required";
    }

    if (
      formData.visitStart &&
      formData.visitEnd &&
      new Date(formData.visitEnd) <= new Date(formData.visitStart)
    ) {
      newErrors.visitEnd = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        mobileNumber: formData.mobileNumber.replace(/\D/g, ""),
        visitStart: new Date(formData.visitStart).toISOString(),
        visitEnd: new Date(formData.visitEnd).toISOString(),
      };

      if (isEdit && visitor) {
        const id = visitor._id || visitor.id;
        if (!id) return;
        await updateVisitor(id, submitData);
        toast.success("Visitor updated successfully");
      } else {
        await createVisitor(submitData);
        toast.success("Visitor registered successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Error saving visitor:", error);
      toast.error(error.response?.data?.resMessage || "Failed to save visitor");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isEdit ? "Update visitor" : "Pre-register visitor / vendor"}
      </h2>
      <form onSubmit={handleSubmit} id="visitorForm" className="px-6 pb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => {
                setFormData({ ...formData, fullName: e.target.value });
                setErrors({ ...errors, fullName: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.fullName ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter full name"
              required
            />
            {errors.fullName && (
              <span className="text-red-500 text-xs">{errors.fullName}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Visitor Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.visitorType}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  visitorType: e.target.value as VisitorType,
                });
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
              required
            >
              <option value="Patient Attendant">Patient Attendant</option>
              <option value="Vendor">Vendor</option>
              <option value="Guest">Guest</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                ID Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.idType}
                onChange={(e) => {
                  setFormData({ ...formData, idType: e.target.value });
                  setErrors({ ...errors, idType: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.idType ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="Aadhaar">Aadhaar</option>
                <option value="PAN">PAN</option>
                <option value="Passport">Passport</option>
                <option value="Driving License">Driving License</option>
                <option value="Other">Other</option>
              </select>
              {errors.idType && (
                <span className="text-red-500 text-xs">{errors.idType}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                ID Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.idNumber}
                onChange={(e) => {
                  setFormData({ ...formData, idNumber: e.target.value });
                  setErrors({ ...errors, idNumber: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.idNumber ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter ID number"
                required
              />
              {errors.idNumber && (
                <span className="text-red-500 text-xs">{errors.idNumber}</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.mobileNumber}
              onChange={(e) => {
                setFormData({ ...formData, mobileNumber: e.target.value });
                setErrors({ ...errors, mobileNumber: "" });
              }}
              className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                errors.mobileNumber ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="10-digit mobile (spaces allowed)"
              required
            />
            {errors.mobileNumber && (
              <span className="text-red-500 text-xs">{errors.mobileNumber}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Host Department <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.hostDepartmentId}
              onChange={(e) => {
                const selectedDept = departments.find(
                  (d) => d._id === e.target.value
                );
                setFormData({
                  ...formData,
                  hostDepartmentId: e.target.value,
                  hostDepartment: selectedDept?.departmentName || "",
                });
                setErrors({ ...errors, hostDepartment: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.hostDepartment ? "border-red-500" : "border-gray-300"
              }`}
              required
            >
              <option value="">Select department</option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.departmentName}
                </option>
              ))}
            </select>
            {errors.hostDepartment && (
              <span className="text-red-500 text-xs">{errors.hostDepartment}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Host contact / person (purpose mapping)
            </label>
            <input
              type="text"
              value={formData.hostName || ""}
              onChange={(e) => setFormData({ ...formData, hostName: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
              placeholder="Name of host or receiving officer"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Purpose of visit</label>
            <textarea
              value={formData.purposeOfVisit || ""}
              onChange={(e) =>
                setFormData({ ...formData, purposeOfVisit: e.target.value })
              }
              rows={2}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] resize-y"
              placeholder="e.g. Patient visit, equipment delivery, vendor meeting"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Access Zones <span className="text-red-500">*</span>
            </label>
            <MultiSelectDropdown
              options={zones}
              selectedValues={formData.accessZones}
              onChange={(selected: string[]) => {
                setFormData({ ...formData, accessZones: selected });
                setErrors({ ...errors, accessZones: "" });
              }}
              placeholder="Select access zones"
            />
            {errors.accessZones && (
              <span className="text-red-500 text-xs">{errors.accessZones}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Visit Start <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.visitStart}
                onChange={(e) => {
                  setFormData({ ...formData, visitStart: e.target.value });
                  setErrors({ ...errors, visitStart: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.visitStart ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.visitStart && (
                <span className="text-red-500 text-xs">{errors.visitStart}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Visit End <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.visitEnd}
                onChange={(e) => {
                  setFormData({ ...formData, visitEnd: e.target.value });
                  setErrors({ ...errors, visitEnd: "" });
                }}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.visitEnd ? "border-red-500" : "border-gray-300"
                }`}
                required
              />
              {errors.visitEnd && (
                <span className="text-red-500 text-xs">{errors.visitEnd}</span>
              )}
            </div>
          </div>

          {!isEdit && (
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!formData.idVerified}
                onChange={(e) =>
                  setFormData({ ...formData, idVerified: e.target.checked })
                }
                className="mt-1 rounded border-gray-300"
              />
              <span>
                ID verified at reception (marks government ID checked against physical document)
              </span>
            </label>
          )}
        </div>
      </form>
    </>
  );
};

export default VisitorForm;
